import type { ReactNode } from 'react';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '@repo/shared/common';
import { sessionQueryOptions } from '~/lib/queries/session';
import { resolveLandingRoute } from '~/lib/landing';
import styles from './_auth.module.css';

export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context }) => {
    // Already-signed-in users skip the auth screens — resolved server-side, so
    // they never flash the sign-in form before a render-time redirect.
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
    if (session) {
      throw redirect({ to: await resolveLandingRoute(context.queryClient) });
    }
  },
  component: AuthLayout,
});

function AuthLayout(): ReactNode {
  const { t } = useTranslation();

  // Left value-props panel: the product's pitch, the three outcomes as chips, and
  // three supporting qualities. Right panel (the Outlet) is the sign-in/up card.
  const metrics = [
    t('auth.metricCreditLoss'),
    t('auth.metricPrepaymentRisk'),
    t('auth.metricNetInterestMargin'),
  ];
  const supportingProps = [
    t('auth.valuePropSimple'),
    t('auth.valuePropTransparent'),
    t('auth.valuePropAccurate'),
  ];

  return (
    <div className={styles.split}>
      <div className={styles.hero}>
        {/* Faint oversized monogram — heritage texture, cropped by overflow. */}
        <span className={styles.monogram} aria-hidden>{APP_NAME.charAt(0)}</span>
        <div className={styles.heroInner}>
          <div className={styles.brand}>
            <span className={styles.brandName}>{APP_NAME}</span>
            <span className={styles.brandRule} aria-hidden />
          </div>

          <div className={styles.heroContent}>
            {/* Intentional two-line statement (the break is forced, not wrapped). */}
            <h2 className={styles.tagline}>
              {t('auth.valuePropLine1')}
              <br />
              {t('auth.valuePropLine2')}
            </h2>
            <ul className={styles.metricChips}>
              {metrics.map((metric) => (
                <li key={metric} className={styles.metricChip}>{metric}</li>
              ))}
            </ul>
            <ul className={styles.qualities}>
              {supportingProps.map((prop) => (
                <li key={prop} className={styles.quality}>{prop}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className={styles.formPane}>
        <div className={styles.formContainer}>
          {/* The sign-in / sign-up form (AuthForm) owns its entrance via its own
              <Reveal> cascade on mount — no route-level fade wrapping the Outlet. */}
          <Outlet />
        </div>
      </div>
    </div>
  );
}
